import { globals } from "@client/globals";
import { getImageStyle } from "@client/lib/image";
import { getActualHeight, getActualWidth, parseValue as parseSizeValue } from "@client/lib/values/size";
import {
  type Availability,
  type CompositeSettings,
  type ElementVisibility,
  FieldType,
  type GenericMap,
  type LocalSettings,
  type LogoOptions,
  type MainSettings,
  MediaType,
  ScreenType,
  type TextIcon
} from "@client/types";
import { type PortalAppState } from "@client/types/AppState/portal";
import { type SocialIcon } from "@client/types/Fields";
import { isNullOrUndefined } from "@common/lib/misc";
import { get, isNull, isUndefined, set, uniq, unset } from "lodash";
import { joinDefined } from "./misc";
import { setWithSpread } from "./object";
import { getUpScreen } from "./viewport";

export const mediaTypeToFieldType = (type: MediaType): string => {
  switch (type) {
    case MediaType.Audio:
      return FieldType.Audio;
    case MediaType.Image:
      return FieldType.Image;
    case MediaType.Video:
      return FieldType.Video;
  }
};

export const mergeAvailability = (
  a: Availability | undefined,
  b: Availability | undefined
): Availability | undefined => {
  if (!a && !b) return;

  const adc = typeof a === "object" ? a.disabled : undefined;
  const bdc = typeof b === "object" ? b.disabled : undefined;

  const avc = typeof a === "object" ? a.visible : a;
  const bvc = typeof b === "object" ? b.visible : b;

  const m: Availability = {
    disabled: [adc, bdc]
      .filter((x) => !!x)
      .map((x) => `(${x})`)
      .join("&&"),
    visible: [avc, bvc]
      .filter((x) => !!x)
      .map((x) => `(${x})`)
      .join("&&")
  };

  return m.disabled ? m : m.visible;
};

export const mapGroupProp = (name: string, prop: string | boolean | undefined): string | undefined =>
  typeof prop === "boolean" ? (prop ? name : undefined) : prop;

export const parseSocialIcons = (value: string, allIcons?: TextIcon[]): SocialIcon[] =>
  value
    ? value
        .split("$")
        .filter((item) => !!item)
        .map((part) => {
          const subParts = part.split("|");
          return {
            icon: subParts[0],
            link: subParts[1]
          };
        })
    : allIcons
    ? allIcons.map((i) => ({ icon: i.icon, link: "" }))
    : [];

export const stringifySocialIcons = (icons: SocialIcon[]): string =>
  icons.map((icon) => `${icon.icon}|${icon.link}`).join("$");

export const getVisibility = (visible: boolean | undefined): ElementVisibility => ({
  isVisible: visible !== false
});

export const wrapSettingValue = (type: string, value: any): any => {
  switch (type) {
    case FieldType.Image:
      return {
        original: value,
        resized: value
      };
    default:
      return value;
  }
};

export const unwrapSettingValue = (type: string, value: any): any => {
  switch (type) {
    case FieldType.Highlight:
    case FieldType.Text:
    case FieldType.RichEditor:
    case FieldType.Number: // for backward compatibility
    case FieldType.Color: // for backward compatibility
    case FieldType.Image:
      return value && typeof value === "object" ? value.original : value;
    default:
      return value;
  }
};

export const getLogoOptions = (
  portal: PortalAppState,
  setting: CompositeSettings.Logo | null | undefined,
  isDesktop: boolean
): LogoOptions | undefined => {
  if (!setting) return;

  return {
    fileSrc: setting.type === "custom" ? getImage(setting.file, true) : portal.logoUrl,
    style: getImageStyle(setting.size, {
      padding: setting.padding
    }),
    position: isDesktop ? setting.position : "center", // ???
    title: globals.siteInfo.title
  };
};

export const getCSSBackgroundSize = (sizeStr: string): string => {
  const size = parseSizeValue(sizeStr);
  return `${getActualWidth(size)}px ${getActualHeight(size)}`;
};

export const getUrl = (path: string): string => {
  if (!path || path === "#_") return "";

  return /^(http(s)?:\/)?\/|#/.test(path) ? path : `${window.location.protocol}//${path}`;
};

export const getFontFamilyName = (value: string | undefined): string | undefined => {
  if (!value) return;

  const colonIndex = value.indexOf(":");
  return colonIndex > 0 ? value.substr(0, value.indexOf(":")).replace(/\+/g, " ") : value;
};

export const getScreenSettings = (settings: GenericMap<any>, screen: ScreenType): any =>
  screen === ScreenType.Desktop ? settings._desktop ?? settings : settings[`_${screen}`];

export const getSettingValue = (
  settings: GenericMap<any>,
  path: string | undefined,
  name: string,
  screen: ScreenType
): any => {
  const source = getScreenSettings(settings, screen);
  return get(source, joinDefined(".", path, name));
};

const getDefined = (...args: any) => args.find((x) => !isUndefined(x));

export const getInherittedSettingValue = <T = any>(
  settings: GenericMap<any>,
  path: string | undefined,
  name: string,
  screen: ScreenType
): any => {
  const desktop = getSettingValue(settings, path, name, ScreenType.Desktop);
  if (screen === ScreenType.Desktop) return desktop as T;
  const tablet = getSettingValue(settings, path, name, ScreenType.Tablet);
  if (screen === ScreenType.Tablet) return getDefined(tablet, desktop) as T;
  const mobile = getSettingValue(settings, path, name, ScreenType.Mobile);
  return getDefined(mobile, tablet, desktop) as T;
};

export const getInherittedSettingValueV2 = <T = any>(
  settings: GenericMap<any>,
  localSettings: LocalSettings,
  path: string | undefined,
  name: string,
  screen: ScreenType
): any => {
  const value = localSettings ? getInherittedSettingValue<T>(localSettings, path, name, screen) : undefined;
  return isUndefined(value) ? getInherittedSettingValue<T>(settings, path, name, screen) : value;
};

export const splitFullPath = (fullPath: string): { name: string; path?: string } => {
  const index = fullPath.lastIndexOf(".");
  return index >= 0
    ? {
        name: fullPath.substring(index + 1),
        path: fullPath.substring(0, index)
      }
    : {
        name: fullPath
      };
};

export interface Radius {
  topLeft: string;
  topRight: string;
  bottomRight: string;
  bottomLeft: string;
}
export const expandRadius = (value: string | undefined): Radius | undefined => {
  if (!value) return;

  const sm = value.match(/^\d+(px)|(%)|(em)$/);
  if (sm) return { bottomRight: value, topRight: value, bottomLeft: value, topLeft: value };

  const mm = value.match(/^(\d+(px|em|%)) (\d+(px|em|%))$/);
  if (mm) return { bottomRight: mm[1], topRight: mm[3], topLeft: mm[1], bottomLeft: mm[3] };

  const lm = value.match(/^(\d+(px|em|%)) (\d+(px|em|%)) (\d+(px|em|%)) (\d+(px|em|%))$/);
  if (lm) return { bottomRight: lm[5], topRight: lm[3], topLeft: lm[1], bottomLeft: lm[7] };

  return;
};

/**
 * @example
 *  value: "15px 20px 10px 8px"
 *  shorthand: "{top} {right} {bottom} {left}"
 *
 *  result: {top: "15px", right: "20px", bottom: "10px", left: "8px"}
 */
export type LonghandValue = {
  isMixed: boolean;
  value: GenericMap<any>;
};
export const fromShorthand = (value: string | null, shorthand: string): LonghandValue => {
  const keys = shorthand.split(" ").map((x) => x.replace(/^\{/, "").replace(/\}$/, ""));
  const regex = new RegExp(shorthand.replaceAll("{", "(?<").replaceAll("}", ">.+)"));

  const match = value ? value.match(regex) : null;
  const rv = keys.reduce((res, x) => ({ ...res, [x]: match ? match.groups![x] : value }), {});
  return {
    isMixed: uniq(Object.values(rv)).length > 1,
    value: rv
  };
};

export const toShorthand = (value: GenericMap<any>, shorthand: string, noValue: any): string => {
  const values = Object.values(value);
  if (uniq(values).length === 1) return values[0];

  const shortcutKeys = shorthand.split(" ").map((x) => x.replace(/^\{/, "").replace(/\}$/, ""));
  return shortcutKeys.map((x) => value[x] ?? noValue).join(" ");
};

export const getDateTimeValue = (uv: string): Date | undefined => {
  if (isUndefined(uv) || uv === null) return;

  return new Date(uv);
};

export const getImage = (uv: any, isOriginal: boolean = false): string => {
  if (!uv) return "";
  if (typeof uv === "object") {
    return isOriginal ? uv.original : uv.resized;
  }
  return uv.toString();
};

export const getMedia = (value: string, type: MediaType, isOriginal: boolean = true): string | undefined => {
  switch (type) {
    case MediaType.Image:
      return getImage(value, isOriginal);
    case MediaType.Audio:
      return value;
    case MediaType.Video:
      return value;
    default:
      throw new Error("Unknown media type");
  }
};
export const getMultiselectValue = <T extends string>(value: string): T[] | null => {
  const v = value;
  if (isUndefined(v)) return null;
  if (!v) return [];

  return v.toString().split(",") as T[];
};

export const isValue = (v: any) => !isNull(v) && !isUndefined(v);

const mergeGetValue = (p: string, k: string, t: object, f: object) => {
  const fv = f[k];
  const tv = t[k];
  if (fv instanceof Object && tv instanceof Object) {
    return {
      proceed: true,
      value: { ...tv }
    };
  }

  return {
    proceed: false,
    value: tv ?? fv
  };
};

export const mergeCompositeValues = <T extends {}>(to: Partial<T>, from: Partial<T>): T => {
  const mergeInner = (to: object, from: object, path: string) => {
    if (!to) return;
    if (!from) return;

    for (let key in from) {
      if (from.hasOwnProperty(key)) {
        const v = mergeGetValue(path, key, to, from);
        to[key] = v.value;
        if (v.proceed) {
          mergeInner(to[key], from[key], joinDefined(".", path, key));
        }
      }
    }
  };

  const result = { ...to };

  mergeInner(result, from, "");

  return result as T;
};

export const mergeInheritedValues = <T extends {}>(
  inherited: T | undefined | null,
  settings: Partial<T>
): Partial<T> => {
  if (!inherited) return settings;

  return mergeCompositeValues<T>(settings, inherited);
};

export const hasResponsiveValue = (source: MainSettings, path: string): boolean => {
  let has = false;
  processPossiblyResponsiveValue(source, (settings, screen) => {
    const value = get(settings, path);
    if (!isNullOrUndefined(value)) {
      has = true;
    }
  });
  return has;
};

/** modifies the source */
export const moveResponsiveValue = (
  settings: MainSettings,
  from: string,
  to: string,
  getValue?: (value: any) => any
) => {
  processPossiblyResponsiveValue(settings, (settings, screen) => {
    const value = get(settings, from);

    if (!isNullOrUndefined(value) || getValue) {
      settings = setValue(settings, to, getValue ? getValue(value) : value, screen);
      unset(settings, from);
    }
  });
};

export const setValue = (settings: MainSettings, path: string, val: any, screen: ScreenType): MainSettings => {
  if (screen === ScreenType.Desktop) {
    set(settings._desktop ?? settings, path, val);
  } else {
    if (!settings._desktop && !settings._tablet && !settings._mobile) {
      settings = {
        _desktop: settings,
        version: settings.version
      };
    }
    set(settings, joinDefined(".", `_${screen}`, path), val);
  }
  return settings;
};

export const setValueIfDefined = (settings: MainSettings, path: string, val: any, screen: ScreenType): MainSettings => {
  if (isUndefined(val)) return settings;

  return setValue(settings, path, val, screen);
};

export const unsetValue = (settings: MainSettings, path: string, screen: ScreenType): MainSettings => {
  if (screen === ScreenType.Desktop) {
    unset(settings._desktop ?? settings, path);
  } else {
    if (!settings._desktop && !settings._tablet && !settings._mobile) {
      settings = {
        _desktop: settings
      };
    }
    unset(settings, joinDefined(".", `_${screen}`, path));
  }
  return collapseResponsiveSettings(settings);
};

export const replaceSetting = (
  settings: MainSettings,
  name: string,
  newName: string,
  getNewValue?: (value: any) => any
): MainSettings => {
  processPossiblyResponsiveValue(settings, (x) => {
    if (name in x) {
      x[newName] = getNewValue ? getNewValue(x[name]) : x[name];
      delete x[name];
    }
  });

  return settings;
};

export const removeSetting = (settings: MainSettings, name: string): MainSettings => {
  processPossiblyResponsiveValue(settings, (x) => {
    if (name in x) {
      delete x[name];
    }
  });

  return settings;
};

export const getResponsiveValue = (settings: MainSettings, path: string): any => {
  const rv = {
    _desktop: get(settings._desktop ?? settings, path),
    _tablet: settings._tablet ? get(settings._tablet, path) : undefined,
    _mobile: settings._mobile ? get(settings._mobile, path) : undefined
  };
  if (!rv._tablet) delete rv._tablet;
  if (!rv._mobile) delete rv._mobile;
  return rv._tablet || rv._mobile ? rv : rv._desktop;
};
export const setResponsiveValue = (settings: MainSettings, path: string, val: any): MainSettings => {
  const rs = {
    _desktop: settings._desktop ?? settings,
    _tablet: settings._tablet,
    _mobile: settings._mobile,
    version: settings.version
  };
  const rv = {
    _desktop: val?._desktop ?? val,
    _tablet: val?._desktop ? val?._tablet : val,
    _mobile: val?._desktop ? val?._mobile : val
  };

  delete rs._desktop?.version;
  delete rs._tablet?.version;
  delete rs._mobile?.version;

  const set = (device: string) => {
    if (device === "_desktop" || !isUndefined(rv[device])) {
      rs[device] = setWithSpread(rs[device], path, rv[device]);
    }
  };

  set("_desktop");
  set("_tablet");
  set("_mobile");

  if (get(rs._desktop, path) === get(rs._tablet, path) && get(rs._tablet, path) === get(rs._mobile, path)) {
    unset(rs._tablet, path);
    unset(rs._mobile, path);
  }

  return collapseResponsiveSettings(rs);
};

export const processPossiblyResponsiveValue = (value: any, process: (value: any, screen: ScreenType) => void) => {
  if (typeof value === "object" && ("_desktop" in value || "_tablet" in value || "_mobile" in value)) {
    if ("_desktop" in value) {
      process(value._desktop, ScreenType.Desktop);
    }
    if ("_tablet" in value) {
      process(value._tablet, ScreenType.Tablet);
    }
    if ("_mobile" in value) {
      process(value._mobile, ScreenType.Mobile);
    }
  } else {
    process(value, ScreenType.Desktop);
  }
};

/**
 * Use on the root settings only
 */
export const processSettings = (
  settings: MainSettings,
  get: (settings: GenericMap<any>, screen: ScreenType) => any
): any => {
  const res: GenericMap<any> = {};

  processPossiblyResponsiveValue(settings, (x, s) => {
    res[`_${s}`] = get(x, s);
  });

  return collapseResponsiveSettings(res);
};

export const updatePossiblyResponsiveValue = (
  settings: MainSettings,
  update: (settings: GenericMap<any>, screen: ScreenType) => GenericMap<any>
): MainSettings => {
  if ("_desktop" in settings || "_tablet" in settings || "_mobile" in settings) {
    if ("_desktop" in settings) {
      settings._desktop = update(settings._desktop, ScreenType.Desktop);
    }
    if ("_tablet" in settings) {
      settings._tablet = update(settings._tablet, ScreenType.Tablet);
    }
    if ("_mobile" in settings) {
      settings._mobile = update(settings._mobile, ScreenType.Mobile);
    }
    return settings;
  }

  return update(settings, ScreenType.Desktop);
};

export const isResponsiveValue = (val: any): boolean => {
  return val && typeof val === "object" && ("_desktop" in val || "_tablet" in val || "_mobile" in val);
};

export const collapseResponsiveSettings = (settings: MainSettings): MainSettings => {
  if ("_tablet" in settings && (!settings._tablet || !Object.keys(settings._tablet).length)) delete settings._tablet;
  if ("_mobile" in settings && (!settings._mobile || !Object.keys(settings._mobile).length)) delete settings._mobile;

  if (!settings._tablet && !settings._mobile && settings._desktop)
    return {
      ...settings._desktop,
      version: settings.version
    };
  return settings;
};

const isNoValue = (value: any) => isUndefined(value) || value === null;
const getFirstValue = (...values: any) => values.find((v) => !isNoValue(v));
export const unwrapPossiblyResponsiveValue = <T>(value: T, screen: ScreenType): T | undefined => {
  if (isUndefined(value)) return;

  if (isResponsiveValue(value)) {
    const rv = value as ResponsiveValue<T>;

    switch (screen) {
      case ScreenType.Mobile:
        return getFirstValue(rv.mobile, rv.tablet, rv.desktop);
      case ScreenType.Tablet:
        return getFirstValue(rv.tablet, rv.desktop);
      case ScreenType.Desktop:
        return rv.desktop;
      default:
        throw new Error(`Unsupported screen: ${screen}`);
    }
  }

  return value;
};

/** @deprecated */
export interface ResponsiveValue<T> {
  desktop?: T;
  mobile?: T;
  tablet?: T;
}
/** @deprecated */
export const compatibility = (() => {
  const isResponsiveValue = (val: any): boolean => {
    return val && typeof val === "object" && ("desktop" in val || "tablet" in val || "mobile" in val);
  };

  return {
    isResponsiveValue,
    processValue: (value: any, getValue: (x: any, s: ScreenType) => any): any => {
      if (isResponsiveValue(value)) {
        if ("desktop" in value) value.desktop = getValue(value.desktop, ScreenType.Desktop);
        if ("tablet" in value) value.tablet = getValue(value.tablet, ScreenType.Tablet);
        if ("mobile" in value) value.mobile = getValue(value.mobile, ScreenType.Mobile);

        if (value.mobile === value.tablet) delete value.mobile;
        if (value.tablet === value.desktop) delete value.tablet;

        if (!("mobile" in value) && !("tablet" in value)) return value.desktop;

        return value;
      }

      return getValue(value, ScreenType.Desktop);
    },
    unwrapValue: unwrapPossiblyResponsiveValue
  };
})();

export const addVersion = <T extends object = MainSettings>(
  settings: Omit<T, "version">,
  version: string = globals.version
): T =>
  ({
    ...settings,
    version
  } as T);

export const innerHorizontalAlignToJustifyContent = (value: string) =>
  value === "right" ? "flex-end" : value === "center" ? "center" : value === "justify" ? "stretch" : "flex-start"; // left by default

const mergeCompositeSettingInner = <T extends {}>(
  getValue: (screen: ScreenType) => T | null,
  screen: ScreenType
): T | null => {
  let result = getValue(screen);
  if (!isNull(result)) {
    let nextScreen = getUpScreen(screen);
    while (nextScreen) {
      const sv = getValue(nextScreen);
      if (isNull(sv) && !result) return result;

      if (sv) {
        result = mergeCompositeValues(result, sv) as T;
      }
      nextScreen = getUpScreen(nextScreen);
    }
  }
  return result;
};

export const mergeCompositeSetting = <T extends {}>(
  getValue: (screen: ScreenType) => T | null,
  getLocalValue: ((screen: ScreenType) => T | null) | null,
  screen: ScreenType
): T | null => {
  const localValue = getLocalValue ? mergeCompositeSettingInner(getLocalValue, screen) : undefined;
  if (isNull(localValue)) return localValue;

  const value = mergeCompositeSettingInner(getValue, screen);
  return localValue ? mergeCompositeValues(localValue, (value ?? {}) as Partial<T>) : value;
};

export const getCompositeSetting = <T extends {}>(
  settings: MainSettings,
  path: string | undefined,
  name: string,
  screen: ScreenType
): T | null => mergeCompositeSetting<T>((screen) => getSettingValue(settings, path, name, screen), null, screen);

export const getCompositeSettingV2 = <T extends {}>(
  settings: MainSettings,
  local: LocalSettings,
  path: string | undefined,
  name: string,
  screen: ScreenType
): T | null =>
  mergeCompositeSetting<T>(
    (screen) => getSettingValue(settings, path, name, screen),
    local ? (screen) => getSettingValue(local, path, name, screen) : null,
    screen
  );
